import fetchAllCropsV2, { FETCH_CROPS } from '@dehaat/kisan-app-bl/api/fetchAllCropsV2';
import Crop, { CropsWithCategoryProps } from '@dehaat/kisan-app-bl/models/CropV2';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from '@/context/AuthProvider';
import { UserContext } from '@/context/UserProvider';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import { FarmFourCornerTagPayload } from '@/models/FourCornerFarmTag';
import postFourCornerFarmTag from '@/pages/api/postFourCornerFarmTag';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import CropSelectionModal from '../CropSelectionModal';
import TakeFarmFourCornersPhoto, { CornerData } from '../geoTag/TakeFarmFourCornersPhoto';
import LoginScreen from '../login/LoginScreen';
interface satelliteMonitoringType {
  farmName: string;
  image: Record<number, CornerData>;
  crop?: Crop;
}
interface Props {
  onContinue: (id: number) => void;
  onBackClick: VoidFunction;
}
const FourCornersFarmTag = ({
  onBackClick,
  onContinue
}: Props) => {
  const {
    t
  } = useTranslation('satellite');
  const [activeStep, setActiveStep] = useState(1);
  const [satelliteMonitoringData, setSatelliteMonitoringData] = useState<satelliteMonitoringType>({
    farmName: '',
    image: {}
  });
  const [userCrops, setUserCrops] = useState<CropsWithCategoryProps[]>([]);
  const [isCropsLoading, setIsCropsLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const {
    user
  } = useContext(UserContext);
  const {
    locale
  } = useRouter();
  const {
    isAuthenticated
  } = useContext(AuthContext);
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const buildFourCornerPayload = (farmName: string, images: Record<number, CornerData>, cropId: number): FarmFourCornerTagPayload => {
    const getCornerSafe = (id: number) => {
      const corner = images[id];
      if (!corner?.location || !corner?.filepath) return null;
      return {
        point: {
          type: 'Point' as const,
          coordinates: [Number(corner.location.longitude), Number(corner.location.latitude)] as [number, number]
        },
        accuracy: Number(corner.location.accuracy || 0),
        photo: corner.filepath
      };
    };
    const c1 = getCornerSafe(1);
    const c2 = getCornerSafe(2);
    const c3 = getCornerSafe(3);
    const c4 = getCornerSafe(4);

    // pick first available image as main photo
    const mainPhoto = c1?.photo || c2?.photo || c3?.photo || c4?.photo || '';
    if (!mainPhoto) {
      throw new Error('No valid corner images found');
    }
    return {
      name: farmName,
      ...(c1 && {
        plot_corner_x1: c1.point,
        plot_corner_x1_accuracy: c1.accuracy
      }),
      ...(c2 && {
        plot_corner_x2: c2.point,
        plot_corner_x2_accuracy: c2.accuracy
      }),
      ...(c3 && {
        plot_corner_y1: c3.point,
        plot_corner_y1_accuracy: c3.accuracy
      }),
      ...(c4 && {
        plot_corner_y2: c4.point,
        plot_corner_y2_accuracy: c4.accuracy
      }),
      photo: mainPhoto,
      report_opt_in: true,
      corner_images: {
        ...(c1 && {
          plot_corner_x1: c1.photo
        }),
        ...(c2 && {
          plot_corner_x2: c2.photo
        }),
        ...(c3 && {
          plot_corner_y1: c3.photo
        }),
        ...(c4 && {
          plot_corner_y2: c4.photo
        })
      },
      crop: cropId
    } as FarmFourCornerTagPayload;
  };
  useEffect(() => {
    const getAllCrops = async () => {
      if (userCrops.length === 0) {
        if (isAuthenticated) {
          setIsCropsLoading(true);
          try {
            const allCrops = await fetchAllCropsV2(locale, FETCH_CROPS.ALL_CROPS);
            if (allCrops && allCrops.length > 0) {
              setUserCrops(allCrops);
            }
          } catch {
            //TODO: Handle Error
          } finally {
            setIsCropsLoading(false);
          }
        }
      }
    };
    if (activeStep == 1) getAllCrops();
  }, [isAuthenticated, userCrops.length, locale, activeStep]);
  const postDetails = useCallback(async () => {
    const {
      farmName,
      image,
      crop
    } = satelliteMonitoringData;
    const cropId = crop?.id;
    if (!cropId) return;
    const validCornerCount = Object.values(image).filter(corner => corner.filepath && corner.location).length;
    if (validCornerCount < 3) {
      console.warn('At least 3 valid corners are required');
      return;
    }
    try {
      const payload = buildFourCornerPayload(farmName, image, cropId);
      const res = await postFourCornerFarmTag(payload);
      if (res) onContinue(res.id);
    } catch (e) {
      console.error(e);
    }
  }, [satelliteMonitoringData, onContinue]);
  const handleCropAdded = (crops: Crop[]) => {
    trackCustomEvent(CustomEventTypes.SATELLITE_CROP_ADDED, JSON.stringify({
      FarmerName: user?.full_name,
      FarmerNumber: user?.phone_number,
      cropName: crops[0].name,
      cropId: crops[0].id,
      source: 'Satellite Tagging'
    }));
    setSatelliteMonitoringData(prev => ({
      ...prev,
      crop: crops[0]
    }));
  };
  useEffect(() => {
    if (satelliteMonitoringData.crop?.id) {
      postDetails();
    }
  }, [postDetails, satelliteMonitoringData.crop?.id]);
  useEffect(() => {
    if (!isAuthenticated) {
      setShowLogin(true);
    }
  }, [activeStep, isAuthenticated]);
  return <>
      {showLogin && <LoginScreen isPopup onBack={() => setActiveStep(0)} onLogin={() => {
      setShowLogin(false);
    }} />}
      {activeStep == 1 && !showLogin && <TakeFarmFourCornersPhoto onBackClick={() => {
      setActiveStep(1);
      onBackClick();
    }} onContinue={(farmName, images) => {
      setSatelliteMonitoringData(prev => ({
        ...prev,
        farmName,
        image: images
      }));
      setActiveStep(2);
    }} />}
      <CropSelectionModal isLoading={isCropsLoading} show={activeStep == 2} currentCrops={[]} allCrops={userCrops} onContinue={handleCropAdded} heading={t('select_farm_crop')} onBack={() => setActiveStep(1)} isSingleSelect data-sentry-element="CropSelectionModal" data-sentry-source-file="FourCornersFarmTag.tsx" />
    </>;
};
export default FourCornersFarmTag;